import { useLayoutEffect } from 'react';
import { useSetRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { useLiveRef } from '@gitbook/util-react-hooks';

import { getSpaceFromOrgAllContent } from '../../Components';
import { expandedContentItemsAtom } from './Spaces';

/**
 * Auto expand collections containing the space when navigating to a space.
 */
export function useAutoExpandCollections(props: {
    orgAllContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
}) {
    const { orgAllContent, screen } = props;

    const setExpandedContentItemKeys = useSetRecoilState(expandedContentItemsAtom);

    const contentRef = useLiveRef(orgAllContent);
    const screenSpace = 'space' in screen && screen.space ? screen.space : undefined;
    useLayoutEffect(() => {
        if (!screenSpace) {
            return;
        }

        const expandedCollections = getSpaceFromOrgAllContent(
            contentRef.current,
            screenSpace
        )?.ancestors?.map((ancestor) => ancestor.collection);

        if (expandedCollections && expandedCollections.length > 0) {
            setExpandedContentItemKeys((prev) => {
                const newKeys = new Set(prev);
                for (const collection of expandedCollections) {
                    newKeys.add(collection.id);
                }
                return newKeys;
            });
        }
    }, [screenSpace, setExpandedContentItemKeys, contentRef]);
}
